import { createHttpService } from 'src/apis/ApiService';
import { TPaginationParams } from 'src/apis/__shared/shared.interfaces';
import {
  mockGuardianEvents,
  mockGuardianMessages,
} from 'src/__mocks__/guardian/guardianEventsCommunication.mock';
import isAppWithMock from 'src/utils/isAppWithMock';

export type GuardianEvent = {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  type: 'school' | 'class' | 'sports' | 'cultural';
  status: 'upcoming' | 'ongoing' | 'completed';
};

export type GuardianMessage = {
  id: string;
  from: string;
  subject: string;
  message: string;
  date: string;
  read: boolean;
  priority: 'low' | 'medium' | 'high';
};

const useGuardianEventsCommunicationService = () => {
  const httpAPI = createHttpService();
  const baseGuardianPath = '/guardian/events-communication';
  const withMockData = isAppWithMock();

  const getEvents = (
    studentId: string,
    params?: TPaginationParams & { type?: string; status?: string }
  ): Promise<{ data: GuardianEvent[]; count: number }> => {
    if (withMockData) {
      let filteredData = [...mockGuardianEvents];
      
      if (params?.type) {
        filteredData = filteredData.filter((e) => e.type === params.type);
      }
      if (params?.status) {
        filteredData = filteredData.filter((e) => e.status === params.status);
      }
      
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : filteredData.length;
      const paginatedData = filteredData.slice(start, end);
      
      return Promise.resolve({
        data: paginatedData,
        count: filteredData.length,
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
        ...(params?.type && { type: params.type }),
        ...(params?.status && { status: params.status }),
      },
    };

    return httpAPI
      .get<{ data: GuardianEvent[]; count: number }>(
        `${baseGuardianPath}/events`,
        requestObj
      )
      .then((response) => {
        const result = response;
        if (Array.isArray(result)) {
          return { data: result, count: result.length };
        }
        return result as { data: any[]; count: number };
      })
      .catch((error) => {
        console.error('Error fetching guardian events:', error);
        return { data: [], count: 0 };
      });
  };

  const getMessages = (
    studentId: string,
    params?: TPaginationParams & { read?: boolean; priority?: string }
  ): Promise<{ data: GuardianMessage[]; count: number }> => {
    if (withMockData) {
      let filteredData = [...mockGuardianMessages];
      
      if (params?.read !== undefined) {
        filteredData = filteredData.filter((m) => m.read === params.read);
      }
      if (params?.priority) {
        filteredData = filteredData.filter((m) => m.priority === params.priority);
      }
      
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : filteredData.length;
      const paginatedData = filteredData.slice(start, end);
      
      return Promise.resolve({
        data: paginatedData,
        count: filteredData.length,
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
        ...(params?.read !== undefined && { read: params.read }),
        ...(params?.priority && { priority: params.priority }),
      },
    };

    return httpAPI
      .get<{ data: GuardianMessage[]; count: number }>(
        `${baseGuardianPath}/messages`,
        requestObj
      )
      .then((response) => {
        const result = response;
        if (Array.isArray(result)) {
          return { data: result, count: result.length };
        }
        return result as { data: any[]; count: number };
      })
      .catch((error) => {
        console.error('Error fetching guardian messages:', error);
        return { data: [], count: 0 };
      });
  };

  const markMessageAsRead = (
    studentId: string,
    messageId: string
  ): Promise<{ success: boolean }> => {
    if (withMockData) {
      return Promise.resolve({ success: true });
    }

    return httpAPI
      .put<{ success: boolean }>(
        `${baseGuardianPath}/messages/${messageId}/read`,
        { studentId }
      )
      .then((response) => response as { success: boolean })
      .catch((error) => {
        console.error('Error marking message as read:', error);
        throw error;
      });
  };

  return {
    getEvents,
    getMessages,
    markMessageAsRead,
  };
};

export default useGuardianEventsCommunicationService;
